import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  CalendarCheck,
  LayoutDashboard,
  FileText,
  Calendar,
  Settings,
  CreditCard,
  LogOut,
  Menu,
} from 'lucide-react';
import { useAuth } from '@/App';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, code: '01' },
  { path: '/renewals',  label: 'Renewals',  icon: FileText,        code: '02' },
  { path: '/calendar',  label: 'Calendar',  icon: Calendar,        code: '03' },
  { path: '/billing',   label: 'Billing',   icon: CreditCard,      code: '04' },
  { path: '/settings',  label: 'Settings',  icon: Settings,        code: '05' },
];

export const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const NavLink = ({ item, onClick }) => {
    const isActive = location.pathname === item.path;
    return (
      <Link
        to={item.path}
        onClick={onClick}
        className={`group flex items-center gap-3 px-3 py-2.5 text-sm font-medium border-l-2 transition-all ${
          isActive
            ? 'border-respondent bg-cream text-ink'
            : 'border-transparent text-ink-600 hover:bg-cream/60 hover:text-ink'
        }`}
        data-testid={`nav-${item.label.toLowerCase()}`}
      >
        <span className={`font-mono text-[10px] tracking-widest ${isActive ? 'text-respondent' : 'text-ink-600/60'}`}>
          {item.code}
        </span>
        <item.icon className="w-4 h-4" />
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-paper text-ink">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-paper border-r border-ink-200">
          <div className="flex items-center gap-2.5 h-16 px-5 border-b border-ink-200">
            <div className="w-7 h-7 bg-ink flex items-center justify-center">
              <CalendarCheck className="w-4 h-4 text-paper" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">Enota</span>
          </div>

          <div className="px-5 pt-6 pb-3">
            <p className="eyebrow">NAVIGATION</p>
          </div>
          <nav className="flex-1 px-2 space-y-0.5">
            {navItems.map((item) => <NavLink key={item.path} item={item} />)}
          </nav>

          <div className="p-4 border-t border-ink-200">
            <div className="flex items-center gap-3 mb-4">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-none border border-ink-200" />
              ) : (
                <div className="w-9 h-9 bg-ink flex items-center justify-center">
                  <span className="text-paper font-semibold text-sm">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ink truncate">{user?.name}</p>
                <p className="text-[11px] font-mono text-ink-600 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              className="btn btn-outline w-full text-sm"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-paper/95 backdrop-blur border-b border-ink-200">
        <div className="flex items-center justify-between h-16 px-4">
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-ink flex items-center justify-center">
              <CalendarCheck className="w-4 h-4 text-paper" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">Enota</span>
          </Link>
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 p-0 bg-paper border-l border-ink-200">
              <div className="flex flex-col h-full">
                <div className="flex items-center h-16 px-5 border-b border-ink-200">
                  <span className="font-display font-bold text-lg tracking-tight">Menu</span>
                </div>
                <div className="px-5 pt-4 pb-2">
                  <p className="eyebrow">NAVIGATION</p>
                </div>
                <nav className="flex-1 px-2 space-y-0.5">
                  {navItems.map((item) => (
                    <NavLink key={item.path} item={item} onClick={() => setMobileMenuOpen(false)} />
                  ))}
                </nav>
                <div className="p-4 border-t border-ink-200">
                  <div className="flex items-center gap-3 mb-4">
                    {user?.picture ? (
                      <img src={user.picture} alt={user.name} className="w-9 h-9 border border-ink-200" />
                    ) : (
                      <div className="w-9 h-9 bg-ink flex items-center justify-center">
                        <span className="text-paper font-semibold text-sm">
                          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-ink truncate">{user?.name}</p>
                      <p className="text-[11px] font-mono text-ink-600 truncate">{user?.email}</p>
                    </div>
                  </div>
                  <button className="btn btn-outline w-full text-sm" onClick={handleLogout}>
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 lg:pt-0 min-h-screen bg-paper">
        {children}
      </main>
    </div>
  );
};

export default DashboardLayout;
